import type { SiteBriefStatusType } from '../api.types';

type PresentationChrome = {
  summary: string;
  requirements: string;
  plan: string;
  site: string;
  panelLabel: string;
  heading: string;
  description: string;
  progress: string;
  finished: string;
  contentSaved: string;
  tabsLabel: string;
  summaryCaption: string;
  requirementsCaption: string;
  updating: string;
  saved: string;
  summaryEmptyTitle: string;
  summaryUpdating: string;
  summaryEmpty: string;
  requirementsEmptyTitle: string;
  requirementsUpdating: string;
  requirementsEmpty: string;
  clarify: string;
  suggested: string;
  source: string;
  initialPrompt: string;
  stages: Record<SiteBriefStatusType, { title: string; description: string }>;
};

const CHROME: Record<string, PresentationChrome> = {
  en: {
    summary: 'Summary',
    requirements: 'Requirements',
    plan: 'Site plan',
    site: 'Website',
    panelLabel: 'Your site brief and progress',
    heading: 'Your website, taking shape',
    description: 'From the first idea to the final details.',
    progress: 'YOUR PROGRESS',
    finished: 'Made from your ideas.',
    contentSaved: 'Website content saved',
    tabsLabel: 'Website details',
    summaryCaption: 'YOUR WEBSITE AT A GLANCE',
    requirementsCaption: 'THE DETAILS SO FAR',
    updating: 'Updating…',
    saved: 'Saved',
    summaryEmptyTitle: 'Your idea, in a few words',
    summaryUpdating:
      'Evo is updating your brief. Your summary will appear here shortly.',
    summaryEmpty:
      'Share a little about your website in chat. Your summary updates as you refine the details.',
    requirementsEmptyTitle: 'A place for your details',
    requirementsUpdating:
      'Evo is collecting the details from your latest message.',
    requirementsEmpty:
      'The details you share in chat will appear here in plain language after your next reply.',
    clarify: 'To clarify',
    suggested: 'Suggested',
    source: 'Source',
    initialPrompt: 'Your initial prompt',
    stages: {
      init: {
        title: 'Init',
        description:
          'This brief uses the manual workflow. Start gathering from the Site briefs board to open chat.',
      },
      gather_requirements: {
        title: 'A great site starts here.',
        description:
          'Tell Evo about your business, audience, and the website you have in mind.',
      },
      suggest_requirements: {
        title: 'Suggestions',
        description:
          'Review the suggested details in your brief before planning the pages.',
      },
      build_site_structure: {
        title: 'Site structure',
        description: 'Review your page plan, then create the website from it.',
      },
      generate_site_core: {
        title: 'Site core',
        description:
          'Your website structure is created. Bring its sections to life with generated content.',
      },
      fulfill_vibe_blocks: {
        title: 'Vibe blocks',
        description: 'Your website content has been generated and saved.',
      },
    },
  },
  uk: {
    summary: 'Підсумок',
    requirements: 'Вимоги',
    plan: 'План сайту',
    site: 'Сайт',
    panelLabel: 'Ваш бриф і прогрес',
    heading: 'Ваш сайт набуває форми',
    description: 'Від першої ідеї до останніх деталей.',
    progress: 'ВАШ ПРОГРЕС',
    finished: 'Створено з ваших ідей.',
    contentSaved: 'Вміст сайту збережено',
    tabsLabel: 'Деталі сайту',
    summaryCaption: 'КОРОТКО ПРО ВАШ САЙТ',
    requirementsCaption: 'ЗІБРАНІ ДЕТАЛІ',
    updating: 'Оновлюємо…',
    saved: 'Збережено',
    summaryEmptyTitle: 'Ваша ідея у кількох словах',
    summaryUpdating: 'Evo оновлює бриф. Підсумок незабаром з’явиться тут.',
    summaryEmpty:
      'Розкажіть у чаті про свій сайт. Підсумок оновлюватиметься разом з уточненнями.',
    requirementsEmptyTitle: 'Тут будуть ваші вимоги',
    requirementsUpdating: 'Evo збирає деталі з вашого останнього повідомлення.',
    requirementsEmpty:
      'Після вашої наступної відповіді тут з’являться вимоги, описані простою мовою.',
    clarify: 'Потрібно уточнити',
    suggested: 'Пропозиція',
    source: 'Джерело',
    initialPrompt: 'Ваш початковий запит',
    stages: {
      init: {
        title: 'Початок',
        description:
          'Цей бриф заповнюється вручну. Почніть збір вимог на дошці брифів, щоб відкрити чат.',
      },
      gather_requirements: {
        title: 'Тут починається ваш сайт.',
        description: 'Розкажіть Evo про свій бізнес, аудиторію та задум сайту.',
      },
      suggest_requirements: {
        title: 'Пропозиції',
        description:
          'Перегляньте запропоновані деталі брифу перед плануванням сторінок.',
      },
      build_site_structure: {
        title: 'Структура сайту',
        description:
          'Перегляньте план сторінок, а потім створіть сайт на його основі.',
      },
      generate_site_core: {
        title: 'Основа сайту',
        description:
          'Структуру сайту створено. Наповніть його розділи згенерованим вмістом.',
      },
      fulfill_vibe_blocks: {
        title: 'Наповнення сайту',
        description: 'Вміст вашого сайту згенеровано та збережено.',
      },
    },
  },
  ru: {
    summary: 'Итог',
    requirements: 'Требования',
    plan: 'План сайта',
    site: 'Сайт',
    panelLabel: 'Ваш бриф и прогресс',
    heading: 'Ваш сайт обретает форму',
    description: 'От первой идеи до последних деталей.',
    progress: 'ВАШ ПРОГРЕСС',
    finished: 'Создано из ваших идей.',
    contentSaved: 'Содержимое сайта сохранено',
    tabsLabel: 'Детали сайта',
    summaryCaption: 'КОРОТКО О ВАШЕМ САЙТЕ',
    requirementsCaption: 'СОБРАННЫЕ ДЕТАЛИ',
    updating: 'Обновляем…',
    saved: 'Сохранено',
    summaryEmptyTitle: 'Ваша идея в нескольких словах',
    summaryUpdating: 'Evo обновляет бриф. Итог скоро появится здесь.',
    summaryEmpty:
      'Расскажите в чате о своём сайте. Итог будет обновляться по мере уточнений.',
    requirementsEmptyTitle: 'Здесь будут ваши требования',
    requirementsUpdating: 'Evo собирает детали из вашего последнего сообщения.',
    requirementsEmpty:
      'После вашего следующего ответа здесь появятся требования, описанные простым языком.',
    clarify: 'Нужно уточнить',
    suggested: 'Предложение',
    source: 'Источник',
    initialPrompt: 'Ваш первоначальный запрос',
    stages: {
      init: {
        title: 'Начало',
        description:
          'Этот бриф заполняется вручную. Начните сбор требований на доске брифов, чтобы открыть чат.',
      },
      gather_requirements: {
        title: 'Здесь начинается ваш сайт.',
        description: 'Расскажите Evo о своём бизнесе, аудитории и идее сайта.',
      },
      suggest_requirements: {
        title: 'Предложения',
        description:
          'Проверьте предложенные детали брифа перед планированием страниц.',
      },
      build_site_structure: {
        title: 'Структура сайта',
        description:
          'Проверьте план страниц, затем создайте сайт на его основе.',
      },
      generate_site_core: {
        title: 'Основа сайта',
        description:
          'Структура сайта создана. Наполните его разделы сгенерированным содержимым.',
      },
      fulfill_vibe_blocks: {
        title: 'Наполнение сайта',
        description: 'Содержимое вашего сайта сгенерировано и сохранено.',
      },
    },
  },
  es: {
    summary: 'Resumen',
    requirements: 'Requisitos',
    plan: 'Plan del sitio',
    site: 'Sitio web',
    panelLabel: 'Tu brief y progreso',
    heading: 'Tu sitio va tomando forma',
    description: 'De la primera idea a los últimos detalles.',
    progress: 'TU PROGRESO',
    finished: 'Creado a partir de tus ideas.',
    contentSaved: 'Contenido del sitio guardado',
    tabsLabel: 'Detalles del sitio',
    summaryCaption: 'TU SITIO DE UN VISTAZO',
    requirementsCaption: 'DETALLES RECOPILADOS',
    updating: 'Actualizando…',
    saved: 'Guardado',
    summaryEmptyTitle: 'Tu idea en pocas palabras',
    summaryUpdating:
      'Evo está actualizando tu brief. El resumen aparecerá aquí en breve.',
    summaryEmpty:
      'Cuéntanos sobre tu sitio en el chat. El resumen se actualizará a medida que concretes los detalles.',
    requirementsEmptyTitle: 'Aquí estarán tus requisitos',
    requirementsUpdating:
      'Evo está recopilando los detalles de tu último mensaje.',
    requirementsEmpty:
      'Los detalles que compartas aparecerán aquí en lenguaje sencillo después de tu próxima respuesta.',
    clarify: 'Por aclarar',
    suggested: 'Sugerencia',
    source: 'Fuente',
    initialPrompt: 'Tu solicitud inicial',
    stages: {
      init: {
        title: 'Inicio',
        description:
          'Este brief usa el flujo manual. Inicia la recopilación en el tablero de briefs para abrir el chat.',
      },
      gather_requirements: {
        title: 'Un gran sitio empieza aquí.',
        description:
          'Cuéntale a Evo sobre tu negocio, tu público y el sitio que tienes en mente.',
      },
      suggest_requirements: {
        title: 'Sugerencias',
        description:
          'Revisa las sugerencias del brief antes de planificar las páginas.',
      },
      build_site_structure: {
        title: 'Estructura del sitio',
        description:
          'Revisa el plan de páginas y crea el sitio a partir de él.',
      },
      generate_site_core: {
        title: 'Base del sitio',
        description:
          'La estructura del sitio está creada. Llena sus secciones con contenido generado.',
      },
      fulfill_vibe_blocks: {
        title: 'Contenido del sitio',
        description: 'El contenido de tu sitio se ha generado y guardado.',
      },
    },
  },
};

export function resolvePresentationChrome(
  ...languages: Array<string | undefined>
): PresentationChrome {
  for (const language of languages) {
    const primary = language?.trim().toLowerCase().split(/[-_]/)[0];
    const chrome = primary && CHROME[primary === 'ua' ? 'uk' : primary];
    if (chrome) {
      return chrome;
    }
  }
  return CHROME.en;
}
